import React from 'react';
import { FormattedMessage } from 'react-intl';
import { CircularProgress, Grid, Skeleton } from '@mui/material';
import { TranslationKeys } from '../../../../../translations';
import { TableSkeleton } from '../../../../../components';
import DetailsPanelRow from '../../../../../components/DetailsPanelRow';
import DetailsPanel from '../../../../../components/DetailsPanel';
import ActionButton from '../../../../../components/ActionButton';
import { InsurersTable } from '../../../../Insurers/Components/InsurersTable';
import { EmployersTable } from '../../../../Employers/Components';
import { EditOwnerDialog } from '../../../Components/EditOwnerDialog';
import { DivButtonsContainerStyled, ProgressContainerStyled } from './OwnerInformationTab.styles';
import { OwnerSubOwners } from './OwnerSubOwners';
import { DividerStyled } from '../../OwnerAddPage/OwnerAddPage.styles';
import { useOwnerInformationTab } from './useOwnerInformationTab';
import { OwnerListDialog } from '../OwnerListDialog';

export const OwnerInformationTab = () => {
  const {
    owner,
    isOwnerLoading,
    refetchGetOwnerQuery,
    isOwner,
    isSubOwner,
    subOwners,
    isLoadingSubOwners,
    subOwnersPage,
    subOwnersPageSize,
    subOwnersPageSizeOptions,
    subOwnersTotalCount,
    onSubOwnersPaginationChange,
    employersData,
    isLoadingEmployers,
    subOwnerEmployersPage,
    subOwnerEmployersPageSize,
    insurersLoading,
    insurers,
    page,
    pageSize,
    totalCount,
    editOwnerDialogOpen,
    editOwnerCloseDialog,
    viewSubOwnersDialogOpen,
    viewSubOwnersCloseDialog,
    disabledEditButtonIfLoadingOrNoOwnerOrNoAccess,
    disabledShowSubOwnersButtonIfLoadingOrNoAccess,
    onClickEditButton,
    onClickViewSubOwnersButton,
    onEmployersTablePaginationChange,
    onInsurersTablePaginationChange,
  } = useOwnerInformationTab();

  const insurersTableData = React.useMemo(() => {
    const agencyNumberByInsurerId = new Map((owner?.insurers ?? []).map(i => [i.insurerId, i.agencyNumber]));
    return (insurers || []).map(insurer => ({
      ...insurer,
      agencyNumber: agencyNumberByInsurerId.get(insurer.insurerId) ?? null,
    }));
  }, [insurers, owner?.insurers]);

  const ownerTitle = React.useMemo(() => {
    if (owner?.ownerName) {
      return (
        <span>
          {owner.ownerName}
          {owner.subOwners?.length ? ' : ' + owner.subOwners?.length + ' ' : null}
          {owner.subOwners?.length ? <FormattedMessage id={TranslationKeys.owners_subOwners} /> : null}
        </span>
      );
    }
    return <Skeleton variant={'text'} width={200} />;
  }, [owner]);

  const DetailsPanelBody = React.useMemo(() => {
    const infoRow = {
      [TranslationKeys.owners_ownerName]: owner?.ownerName,
      [TranslationKeys.owners_subOwners]: owner?.subOwners?.length ?? 0,
      [TranslationKeys.owners_address]: owner?.address,
      [TranslationKeys.owners_zipCode]: owner?.zipCode,
      [TranslationKeys.owners_city]: owner?.city,
      [TranslationKeys.owners_ownerEmail]: owner?.ownerEmail,
      [TranslationKeys.owners_ownerPhone]: owner?.ownerPhone,
    };

    const contactRow = {
      [TranslationKeys.owners_contactName]: owner?.contactName,
      [TranslationKeys.owners_ownerEmail]: owner?.contactEmail,
      [TranslationKeys.owners_ownerPhone]: owner?.contactPhone,
    };

    const environmentRow = {
      [TranslationKeys.global_kvkNumber]: owner?.kvkNumber,
    };

    if (!isOwner) {
      // @ts-ignore
      delete infoRow[TranslationKeys.owners_subOwners];
    }

    return (
      <>
        <DetailsPanelRow rows={infoRow} loading={isOwnerLoading} />
        <DividerStyled />
        <DetailsPanelRow rows={contactRow} loading={isOwnerLoading} />
        <DividerStyled />
        <DetailsPanelRow rows={environmentRow} loading={isOwnerLoading} />
      </>
    );
  }, [
    isOwner,
    owner?.address,
    owner?.city,
    owner?.contactEmail,
    owner?.contactName,
    owner?.contactPhone,
    owner?.ownerEmail,
    owner?.ownerName,
    owner?.ownerPhone,
    owner?.subOwners?.length,
    owner?.zipCode,
    owner?.kvkNumber,
    isOwnerLoading,
  ]);

  const DetailsPanelActions = React.useMemo(() => {
    return (
      <DivButtonsContainerStyled>
        {isOwner && (
          <ActionButton
            variant="outlined"
            messageId={TranslationKeys.owners_showSubOwners}
            onClick={onClickViewSubOwnersButton}
            disabled={disabledShowSubOwnersButtonIfLoadingOrNoAccess}
          />
        )}
        <ActionButton
          messageId={TranslationKeys.global_edit}
          disabled={disabledEditButtonIfLoadingOrNoOwnerOrNoAccess}
          onClick={onClickEditButton}
        />
      </DivButtonsContainerStyled>
    );
  }, [
    disabledEditButtonIfLoadingOrNoOwnerOrNoAccess,
    disabledShowSubOwnersButtonIfLoadingOrNoAccess,
    isOwner,
    onClickEditButton,
    onClickViewSubOwnersButton,
  ]);

  const Table = React.useMemo(() => {
    if (isOwner) {
      return (
        <InsurersTable
          insurers={insurersTableData}
          totalCount={totalCount}
          page={page}
          rowsPerPage={pageSize}
          onPaginationChange={onInsurersTablePaginationChange}
          loading={insurersLoading}
        />
      );
    }

    if (isSubOwner) {
      return (
        <EmployersTable
          onPaginationChange={onEmployersTablePaginationChange}
          employers={employersData?.data || []}
          totalCount={employersData?.totalCount ?? 0}
          page={subOwnerEmployersPage}
          rowsPerPage={subOwnerEmployersPageSize}
          loading={isLoadingEmployers}
        />
      );
    }

    return <TableSkeleton />;
  }, [
    insurersLoading,
    insurersTableData,
    pageSize,
    employersData?.data,
    employersData?.totalCount,
    subOwnerEmployersPage,
    subOwnerEmployersPageSize,
    totalCount,
    isLoadingEmployers,
    isOwner,
    isSubOwner,
    onEmployersTablePaginationChange,
    onInsurersTablePaginationChange,
    page,
  ]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Grid container>
            <Grid item xs={12}>
              <DetailsPanel
                header={<FormattedMessage id={TranslationKeys.owners_ownerInformation} />}
                body={DetailsPanelBody}
                actions={DetailsPanelActions}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={6} md={8}>
          <Grid container>
            <Grid item xs={12}>
              {Table}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {editOwnerDialogOpen && !!owner && (
        <EditOwnerDialog
          open={editOwnerDialogOpen}
          onClose={editOwnerCloseDialog}
          owner={owner}
          refetchGetOwnerQuery={refetchGetOwnerQuery}
        />
      )}

      {viewSubOwnersDialogOpen && (
        <OwnerListDialog open={viewSubOwnersDialogOpen} title={ownerTitle} onClose={viewSubOwnersCloseDialog}>
          {isLoadingSubOwners ? (
            <ProgressContainerStyled>
              <CircularProgress />
            </ProgressContainerStyled>
          ) : (
            <OwnerSubOwners
              subOwners={subOwners?.data ?? []}
              page={subOwnersPage}
              pageSize={subOwnersPageSize}
              totalCount={subOwnersTotalCount}
              pageSizeOptions={subOwnersPageSizeOptions}
              onPaginationChange={onSubOwnersPaginationChange}
            />
          )}
        </OwnerListDialog>
      )}
    </>
  );
};
